import React, { useRef, useCallback } from 'react';
import styles from './HomePage.module.css';
import { useAuth } from '../context/AuthContext';
import Layout from './layout/Layout';
import { ActivityFeed, ActivityFeedRef } from './activity';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useForegroundRefresh } from '../hooks/useForegroundRefresh';
import { RefreshIcon } from './ui/icons';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const activityFeedRef = useRef<ActivityFeedRef>(null);
  const firstName = user?.displayName?.trim().split(/\s+/)[0];

  // Handle refresh action
  const handleRefresh = useCallback(async () => {
    if (activityFeedRef.current) {
      await activityFeedRef.current.refresh();
    }
  }, []);

  useForegroundRefresh(
    () => activityFeedRef.current?.refresh({ silent: true }) ?? Promise.resolve(),
    { enabled: !!user },
  );

  // Set up pull-to-refresh
  const { isPulling, pullDistance, isRefreshing } = usePullToRefresh(containerRef, {
    onRefresh: handleRefresh,
    enabled: !!user, // Only enable when user is logged in
  });

  // Calculate refresh indicator properties
  const refreshIndicatorOpacity = Math.min(pullDistance / 80, 1);
  const refreshIndicatorRotation = (pullDistance / 80) * 360;

  return (
    <Layout>
      <div className={styles.homeContainer} ref={containerRef}>
        {/* Pull-to-refresh indicator */}
        {user && (isPulling || isRefreshing) && (
          <div
            className={styles.refreshIndicator}
            style={{
              opacity: isRefreshing ? 1 : refreshIndicatorOpacity,
              transform: `translateY(${isRefreshing ? 0 : pullDistance * 0.5}px)`,
            }}
          >
            <div
              className={`${styles.refreshIcon} ${isRefreshing ? styles.refreshing : ''}`}
              style={{
                transform: isRefreshing ? undefined : `rotate(${refreshIndicatorRotation}deg)`,
              }}
            >
              <RefreshIcon />
            </div>
            {isRefreshing && <span className={styles.refreshText}>Refreshing...</span>}
          </div>
        )}

        {!user && (
          <div className="mobile-page-title-anchor">
            <h1 className={styles.welcomeHeading}>
              Welcome to Organizer
            </h1>
          </div>
        )}

        {user && (
          <div className={styles.feedShell}>
            <section
              className={`${styles.dashboardIntro} mobile-page-header-anchor`}
              aria-label="Homepage feed overview"
            >
              <div className={styles.introCopy}>
                <span className={styles.eyebrow}>Homepage</span>
                <h1 className={styles.feedHeading}>
                  {firstName ? `${firstName}'s daily pulse` : 'Your daily pulse'}
                </h1>
                <p className={styles.feedDescription}>
                  Contact changes, task movement, meetings, and automation signals in one focused stream.
                </p>
              </div>
              <div className={styles.introMeta}>
                <span className={styles.introMetaLabel}>Live feed</span>
                <span className={styles.introMetaDetail}>Pull down to refresh</span>
              </div>
            </section>

            <div className={styles.activitySection}>
              <ActivityFeed ref={activityFeedRef} limit={25} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HomePage;
